import { Theme, css } from '@emotion/react';
import { useIsMutating } from '@tanstack/react-query';
import debugFactory from 'debug';
import React from 'react';

import { DisclaimerMessage } from '~src/ui/disclaimer-message';
import { ListState, useListState } from '~src/ui/list-state';

import { Category, CategoryOption, Choice } from '../../api/api';
import { useLogPosthogEvent } from '../commons/services/posthog-service';
import { useGlobalState, useSessionState } from '../global-state';
import { SelectableCategoryCard } from './category-card';
import { CategoryStateContext } from './category-state';
import { FoldingChildProps, FoldingProvider } from './folding-extension';
import { constructUserChoice, reconstructUserChoice } from './user-choice';
import { UserChoiceProvider } from './user-choice-provider';

const debug = debugFactory('ingrid:user_choice');

export interface CategoryExtensionProps {
  category: Category;
  listState: ListState<Category>;
  selected: boolean;
  selectedChoice: Choice;
  selectedOption: CategoryOption;
  onSelectCategory: () => void;
}

export interface CategoryManagerItemProps {
  category: Category;
  hidden: boolean;
  selected: boolean;
  userChoice: Choice | undefined;
  listState: ListState<Category>;
}

type PreviousChoiceContextType = [Choice | undefined, (choice: Choice) => void];
export const PreviousChoiceContext = React.createContext<PreviousChoiceContextType>(
  undefined as unknown as PreviousChoiceContextType
);

/*
 * The implemenation below provides relation between category, option, and
 * choice. The extensions below can choose on which level they operate.
 */
const CategoryManagerItem: React.FC<CategoryManagerItemProps> = ({
  category,
  hidden,
  listState,
  selected,
  userChoice,
}) => {
  const logPosthogEvent = useLogPosthogEvent();
  /*
   * There is one global choice (aka user choice) but every category has a
   * selected/previous choice that is used for displaying price, delivery
   * time, etc. and for selection if this category gets clicked.
   *
   * We have the following order of option/choice preference:
   * 1. The user choice - if matches current category.
   * 2. The previous choice selected by the user.
   * 3. The preselected choice from backend - if matches current category.
   * 4. First option from the category.
   */
  const [previousChoice, setPreviousChoice] = React.useState<Choice | undefined>(userChoice);
  const savedChoice = userChoice ?? previousChoice;

  /*
   * Continuously guard against a disappearing option.  An option can dispapper
   * virtually any time because of multiple of reasons: postal code change or
   * any change in price rules (e.g. option was valid only for some cart item
   * attribute).
   *
   * Note: Here we have no reason to match delivery time.
   */
  const savedOption = category.options.find(option => {
    return (
      option.shipping_method === savedChoice?.shipping_method &&
      option.location?.id === savedChoice?.location_ref
    );
  });

  const selectedOption = savedOption ?? category.options[0];
  /*
   * Timeslot might get regenerated in backend even on every request.  Always
   * silenty reconstruct the choice using the latest timeslot. Most options
   * usually have only one timeslot, so it is not a problem.
   *
   * The old widget did not try to reconstruct the invalid timeslot but
   * silently dropped the saved option - here we are a bit smarter.
   *
   * Note: This scenario will not work in case of concurrent update, because
   * the regenerated timeslot from previous request has still not reached us
   * yet. Backend will drop such request silently and reset the choice. Old
   * widget has the same behaviour.
   */
  const selectedChoice = savedOption
    ? reconstructUserChoice(category, savedChoice!)
    : constructUserChoice(category);

  if (hidden) {
    return null;
  }

  return (
    <CategoryStateContext.Provider value={category}>
      <PreviousChoiceContext.Provider value={[previousChoice, setPreviousChoice]}>
        <UserChoiceProvider>
          {({ onUpdate }) => (
            <SelectableCategoryCard
              category={category}
              listState={listState}
              selectedChoice={selectedChoice}
              selected={selected}
              selectedOption={selectedOption}
              onSelectCategory={() => {
                onUpdate(selectedChoice);
                logPosthogEvent('Category Changed', selectedChoice);
              }}
            />
          )}
        </UserChoiceProvider>
      </PreviousChoiceContext.Provider>
    </CategoryStateContext.Provider>
  );
};

type OptimisticChoiceContextType = [Choice | undefined, (choice?: Choice) => void];
export const OptimisticChoiceContext = React.createContext<OptimisticChoiceContextType>(
  undefined as unknown as OptimisticChoiceContextType
);

/*
 * The current selection is best represented with a `UserChoice` data
 * structure, which keeps the combined information about category, option,
 * location, and time slot.
 */
export const CategoryManager: React.FC = () => {
  const session = useSessionState();
  const { sessions, recipient } = useGlobalState();
  const isSplitDelivery = sessions.length > 1;

  const backendChoice = session.user_choice || session.preselected_choice;

  /**
   * Choice selected by the user.
   *
   * Here we save the last value selected by user. After last user choice
   * mutations returns this will be synchronized to backend.
   */
  const [optimisticUserChoice, setOptimisticUserChoice] = React.useState<Choice | undefined>(
    undefined
  );

  const noMutating = useIsMutating({ mutationKey: ['user-choice'] });

  React.useEffect(() => {
    if (noMutating === 0) {
      debug('Reset optimistic choice');
      setOptimisticUserChoice(undefined);
    }
  }, [noMutating]);

  const frontendUserChoice = optimisticUserChoice ?? backendChoice;

  const listState = useListState({
    items: session.categories,
    getKey: (category: Category) => category.id,
    defaultSelectedKey: frontendUserChoice.shipping_category,
  });

  debug(`Display choice: ${JSON.stringify(frontendUserChoice)}`);

  /**
   * H&M case with hazmat codes - if the cart contains cart items with hazmat attributes,
   * we won't show any shipping options.
   *
   * Display the message only when categories aren't blurred, i.e. the recipient is defined.
   */

  if (session.categories.length === 0 && !!recipient) {
    return <DisclaimerMessage message="DELIVERY_OPTIONS_UNAVAILABLE" />;
  }

  const renderCategory = ({ category, hidden }: FoldingChildProps) => {
    const selected = category.id === frontendUserChoice.shipping_category;

    return (
      <CategoryManagerItem
        key={category.id}
        category={category}
        hidden={hidden}
        userChoice={selected ? frontendUserChoice : undefined}
        selected={selected}
        listState={listState}
      />
    );
  };

  return (
    <>
      <DisclaimerMessage message={session.disclaimer_message?.content} />
      <OptimisticChoiceContext.Provider value={[optimisticUserChoice, setOptimisticUserChoice]}>
        <SplitDeliveryDivider />
        <div css={styles.wrapper(isSplitDelivery)} role="grid">
          <FoldingProvider userChoice={frontendUserChoice}>{renderCategory}</FoldingProvider>
        </div>
      </OptimisticChoiceContext.Provider>
    </>
  );
};

export const SplitDeliveryDivider: React.FC = () => {
  const { sessions } = useGlobalState();

  if (sessions.length === 1) return null;

  return <div css={styles.splitDeliveryWrapper} />;
};

export const styles = {
  wrapper: (isSplitDelivery: boolean) =>
    isSplitDelivery &&
    css({
      '& >[role=row]:first-of-type > div': {
        borderTop: 'none',
      },
    }),
  splitDeliveryWrapper: (theme: Theme) =>
    css({
      border: `0.5px solid ${theme.neutral85}`,
      margin: '12px -16px',
    }),
};
